import type { StoredSession } from '~/gql/sessions/sessions'
import { MoreHorizontal, PlayCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'
import SessionStatusBadge from '~/pages/backoffice/admin/sessions/components/SessionStatusBadge'

/**
 * Le tableau des sessions, avec le menu d'actions de chaque ligne. Une action
 * sans son callback n'est pas proposée — le porteur n'en a pas le droit (#11) ;
 * sans aucune, le menu disparaît.
 */
export default function SessionsList({ sessions, onEdit, onDuplicate, onDelete, onOpenModule }: {
  sessions: StoredSession[]
  onEdit?: (session: StoredSession) => void
  onDuplicate?: (session: StoredSession) => void
  onDelete?: (session: StoredSession) => void
  onOpenModule?: (moduleId: string) => void
}) {
  const { t } = useTranslation()
  const hasActions = Boolean(onEdit || onDuplicate || onDelete || onOpenModule)

  return (
    <Table aria-label={t('common.routing.sessions')}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('common.name')}</TableHead>
          <TableHead>{t('common.module')}</TableHead>
          <TableHead {...tourAnchor('sessions.trainers')}>{t('sessions.trainers.label')}</TableHead>
          <TableHead>{t('common.participants')}</TableHead>
          <TableHead>{t('common.status')}</TableHead>
          <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.map((s) => {
          const firstModule = s.modules?.[0]?.module
          const moduleCount = s.modules?.length ?? 0
          return (
            <TableRow key={s.id}>
              <TableCell className="font-medium">
                <Link to={`/back-office/sessions/${s.id}`} className="underline-offset-4 hover:underline" aria-label={t('sessionDetail.open', { name: s.name })}>
                  {s.name}
                </Link>
                {(s.tags?.length ?? 0) > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {s.tags?.map(tag => <Badge key={tag.id} variant="outline" className="font-normal">{tag.name}</Badge>)}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {/* Le titre du premier module suffit à identifier le
                    parcours ; le compte dit le reste sans allonger
                    la colonne. Le détail est dans le modal. */}
                <span className="block max-w-[18rem] truncate">
                  {firstModule?.title ?? '—'}
                </span>
                {moduleCount > 1 && (
                  <span className="text-xs text-muted-foreground">
                    {t('sessions.path.count', { count: moduleCount })}
                    {s.progressionMode === 'SEQUENTIAL' && ` · ${t('sessions.progression.sequential')}`}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {(s.trainers?.length ?? 0) > 0
                  ? s.trainers?.map(userDisplayName).join(', ')
                  : <span className="text-muted-foreground">—</span>}
              </TableCell>
              <TableCell>{s.enrolledUsers?.length ?? 0}</TableCell>
              <TableCell>
                <SessionStatusBadge status={s.status} {...tourAnchor('sessions.status')} />
              </TableCell>
              <TableCell className="text-right">
                {hasActions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="size-8 p-0" {...tourAnchor('sessions.actions')}>
                        <span className="sr-only">{t('common.actionFor', { action: t('common.action'), name: s.name })}</span>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(s)}>
                          {t('common.update')}
                        </DropdownMenuItem>
                      )}
                      {onDuplicate && (
                        <DropdownMenuItem onClick={() => onDuplicate(s)}>
                          {t('sessions.duplicate')}
                        </DropdownMenuItem>
                      )}
                      {firstModule && onOpenModule && (
                        <DropdownMenuItem onClick={() => onOpenModule(firstModule.id)}>
                          <PlayCircle className="mr-2 size-4" />
                          {t('sessions.openModule')}
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => onDelete(s)}
                        >
                          {t('common.delete')}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
